"use client";

import { CSSProperties, JSX, ReactNode } from "react";
import { type Translate, useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import type { ModuleCategory, ModuleProgressBarData } from "@/types/modules";
import { renderInlineMarkdown, stripInlineMarkdown } from "@/utils/inline-markdown";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import { importsTokenScope } from "./imports-token-scope";
import {
  resolveCategoryColor,
  resolveCategoryLabel,
} from "./shared/category-resolve";

const tabular: CSSProperties = { fontVariantNumeric: "tabular-nums" };

type ProgressStatus = "on-track" | "at-risk" | "blocked";

interface ProgressItem {
  id: string;
  label: string;
  value: number;
  subtext: string;
  status?: ProgressStatus;
  categoryId?: string;
}

type CompositionSegments = Record<string, number>;

interface CompositionItem {
  id: string;
  label: string;
  segments: CompositionSegments;
  categoryId?: string;
}

type CompositionCategory = {
  id: string;
  label: string;
  color: string;
  countColor: string;
  labelColor: string;
  hideWhenZero: boolean;
};

function legacyProgressCategories(t: Translate): ModuleCategory[] {
  return [
    { id: "on-track", label: t("on track"), color: "var(--emerald, #10b981)" },
    { id: "at-risk", label: t("at risk"), color: "var(--amber, #f59e0b)" },
    { id: "blocked", label: t("blocked"), color: "var(--rose, #f43f5e)" },
  ];
}

function legacyProgressCategoriesGrayscale(t: Translate): ModuleCategory[] {
  return [
    { id: "on-track", label: t("on track"), color: "var(--status-2)" },
    { id: "at-risk", label: t("at risk"), color: "var(--status-neutral)" },
    { id: "blocked", label: t("blocked"), color: "var(--status-1)" },
  ];
}

const legacyCompositionCategories = (t: Translate): CompositionCategory[] => [
  {
    id: "done",
    label: t("done"),
    color: "var(--status-2)",
    countColor: "var(--muted)",
    labelColor: "var(--subtle)",
    hideWhenZero: false,
  },
  {
    id: "review",
    label: t("in review"),
    color: "var(--status-neutral)",
    countColor: "var(--muted)",
    labelColor: "var(--subtle)",
    hideWhenZero: false,
  },
  {
    id: "blocked",
    label: t("blocked"),
    color: "var(--amber, #f59e0b)",
    countColor: "var(--amber, #f59e0b)",
    labelColor: "var(--amber, #f59e0b)",
    hideWhenZero: true,
  },
  {
    id: "todo",
    label: t("todo"),
    color: "var(--track)",
    countColor: "var(--muted)",
    labelColor: "var(--subtle)",
    hideWhenZero: false,
  },
];

const legacyCompositionCategoriesGrayscale = (t: Translate): CompositionCategory[] =>
  legacyCompositionCategories(t).map((cat) =>
    cat.id === "blocked"
      ? { ...cat, color: "var(--status-1)", countColor: "var(--fg)", labelColor: "var(--fg)" }
      : cat,
  );

function toCompositionCategories(
  t: Translate,
  categories?: ModuleCategory[],
  grayscale?: boolean,
): CompositionCategory[] {
  if (!categories || categories.length === 0) {
    return grayscale ? legacyCompositionCategoriesGrayscale(t) : legacyCompositionCategories(t);
  }
  return categories.map((cat) => ({
    id: cat.id,
    label: cat.label,
    color: cat.color,
    countColor: cat.color,
    labelColor: "var(--subtle)",
    hideWhenZero: false,
  }));
}

const PROGRESS_DEMO: ProgressItem[] = [
  { id: "auth",       label: "Auth v2 rewrite",     value: 82, subtext: "3 days left",        status: "on-track" },
  { id: "billing",    label: "Billing overhaul",    value: 45, subtext: "12 days left",       status: "on-track" },
  { id: "mobile",     label: "Mobile companion",    value: 68, subtext: "5 days left",        status: "at-risk"  },
  { id: "analytics",  label: "Analytics dashboard", value: 92, subtext: "1 day left",         status: "on-track" },
  { id: "onboarding", label: "Onboarding v3",       value: 12, subtext: "Started 2 days ago", status: "on-track" },
];

const COMPOSITION_DEMO: CompositionItem[] = [
  { id: "frontend", label: "Frontend refactor", segments: { done: 45, review: 12, blocked: 3, todo: 20 } },
  { id: "backend",  label: "Backend migration", segments: { done: 28, review: 4,  blocked: 0, todo: 68 } },
  { id: "infra",    label: "Infra hardening",   segments: { done: 22, review: 6,  blocked: 2, todo: 15 } },
  { id: "design",   label: "Design system v2",  segments: { done: 18, review: 0,  blocked: 0, todo: 8  } },
  { id: "docs",     label: "Docs revamp",       segments: { done: 8,  review: 2,  blocked: 0, todo: 32 } },
];

function OverviewGrid({
  maxRows = 3,
  minColWidth = 240,
  maxColWidth = 256,
  gap = "1.5rem 2.5rem",
  children,
}: {
  maxRows?: number;
  minColWidth?: number;
  maxColWidth?: number;
  gap?: string;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateRows: `repeat(${maxRows}, auto)`,
        gridAutoFlow: "column",
        gridAutoColumns: `minmax(${minColWidth}px, ${maxColWidth}px)`,
        gap,
      }}
    >
      {children}
    </div>
  );
}

function ProgressRow({
  item,
  categories,
  grayscale,
}: {
  item: ProgressItem;
  categories?: ModuleCategory[];
  grayscale?: boolean;
}) {
  const { t } = useViewerLanguage();
  const free = Boolean(categories && categories.length > 0);
  const cats = free
    ? (categories as ModuleCategory[])
    : grayscale
      ? legacyProgressCategoriesGrayscale(t)
      : legacyProgressCategories(t);
  const key = free ? item.categoryId : item.status;
  const color = resolveCategoryColor(key, cats, "var(--muted-foreground)");
  const statusLabel = resolveCategoryLabel(key, cats, "");
  const value = Math.max(0, Math.min(100, item.value));

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <div className={`text-sm text-[var(--fg)] ${MODULE_LABEL_WRAP.row}`}>
          {stripInlineMarkdown(item.label)}
        </div>
        <div className="text-sm font-medium" style={{ color, ...tabular }}>
          {value}%
        </div>
      </div>
      <div className="h-1 rounded-full bg-[var(--track)] overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${value}%`, background: color }}
        />
      </div>
      <div className="flex items-baseline justify-between mt-2">
        <div className="text-xs text-[var(--subtle)]">{renderInlineMarkdown(item.subtext)}</div>
        <div className="text-xs" style={{ color }}>{statusLabel}</div>
      </div>
    </div>
  );
}

function CompositionRow({
  item,
  categories,
}: {
  item: CompositionItem;
  categories: CompositionCategory[];
}) {
  const { t } = useViewerLanguage();
  const counts = categories.map((cat) => ({
    cat,
    count: item.segments?.[cat.id] ?? 0,
  }));
  const total = counts.reduce((sum, entry) => sum + entry.count, 0);
  const leadCount = counts[0]?.count ?? 0;

  let offset = 0;
  const slices = counts.map(({ cat, count }) => {
    const width = total > 0 ? (count / total) * 100 : 0;
    const left = offset;
    offset += width;
    return { cat, left, width };
  });

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <div className={`text-sm text-[var(--fg)] ${MODULE_LABEL_WRAP.row}`}>
          {stripInlineMarkdown(item.label)}
        </div>
        <div className="text-sm text-[var(--muted)]" style={tabular}>
          <span className="text-[var(--fg)] font-medium">{leadCount}</span>
          <span className="text-[var(--subtle)]"> / {t("{count} Tasks", { count: total })}</span>
        </div>
      </div>
      <div className="relative h-1.5 rounded-full bg-[var(--track)] overflow-hidden">
        {slices.map(({ cat, left, width }) => (
          <div
            key={cat.id}
            className="absolute inset-y-0"
            style={{ left: `${left}%`, width: `${width}%`, background: cat.color }}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs">
        {counts.map(({ cat, count }) =>
          cat.hideWhenZero && count === 0 ? null : (
            <span key={cat.id} style={{ color: cat.labelColor }}>
              <span style={{ color: cat.countColor, ...tabular }}>{count}</span>{" "}
              {cat.label}
            </span>
          ),
        )}
      </div>
    </div>
  );
}

export default function ModuleProgressBars({
  data,
  grayscale,
  accent,
  className,
}: {
  data?: ModuleProgressBarData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  return (
    <SwipeScroll
      className={cn(
        "w-full [mask-image:linear-gradient(to_right,transparent,black_2%,black_98%,transparent)]",
        className,
      )}
    >
      <div className="w-max pr-10" style={importsTokenScope(grayscale, accent)}>
        {data?.variant === "composition" ? (
          <OverviewGrid>
            {(data.items as CompositionItem[]).map((item) => (
              <CompositionRow
                key={item.id}
                item={item}
                categories={toCompositionCategories(t, data.categories, grayscale)}
              />
            ))}
          </OverviewGrid>
        ) : data?.variant === "progress" ? (
          <OverviewGrid>
            {(data.items as ProgressItem[]).map((item) => (
              <ProgressRow key={item.id} item={item} categories={data.categories} grayscale={grayscale} />
            ))}
          </OverviewGrid>
        ) : (
          <OverviewGrid>
            {PROGRESS_DEMO.map((item) => (
              <ProgressRow key={item.id} item={item} grayscale={grayscale} />
            ))}
          </OverviewGrid>
        )}
      </div>
    </SwipeScroll>
  );
}
